(() => {
  'use strict';

  const configNode = document.getElementById('OttVpsConfig');
  if (!configNode) return;

  let config;
  try { config = JSON.parse(configNode.textContent || '{}'); } catch (_) { return; }
  const popularity = config.popularity || {};
  if (!popularity.enabled || !popularity.baseUrl) return;

  const rootsByKey = new Map();
  const stateByKey = new Map();
  const busyKeys = new Set();
  const viewQueue = new Set();
  const viewTimers = new WeakMap();
  const observedRoots = new WeakSet();
  const cacheKey = 'ottrecord-popularity-v1-cache';
  const viewedKey = 'ottrecord-popularity-v1-viewed';
  const sessionKey = 'ottrecord-popularity-v1-session';
  const sessionStore = (() => { try { return sessionStorage; } catch (_) { return null; } })();
  const viewedMemory = new Set();
  const isEnglish = (document.documentElement.lang || '').toLowerCase().startsWith('en');
  const copy = isEnglish ? { views: 'views', favorites: 'network favorites', popularity: 'OTTRecord popularity', like: 'Like on OTTRecord', favorite: 'Add to network favorites' } : { views: 'vues', favorites: 'favoris r\u00e9seau', popularity: 'Popularit\u00e9 OTTRecord', like: 'Aimer sur le r\u00e9seau OTTRecord', favorite: 'Ajouter aux favoris du r\u00e9seau OTTRecord' };
  const numberFormatter = new Intl.NumberFormat(isEnglish ? 'en-US' : 'fr-FR', { notation: 'compact', maximumFractionDigits: 1 });
  let statsRetryCount = 0;
  let statsRetryTimer = null;
  let flushTimer;
  let viewFlushRunning = false;
  let viewFailures = 0;
  let fetchTask;
  let fetchTaskIsIdle = false;
  let lastFetchSignature = '';
  let viewObserver;
  let csrfToken = '';

  const entityKey = (root) => `${root.dataset.ottEntityType}:${root.dataset.ottEntityId}`;
  const splitKey = (key) => {
    const separator = key.indexOf(':');
    return { type: key.slice(0, separator), id: key.slice(separator + 1) };
  };
  const safeInteger = (value) => Math.max(0, Math.round(Number(value) || 0));
  const safeMetric = (value) => {
    if (value === null || value === undefined || value === '') return null;
    const number = Number(value);
    return Number.isFinite(number) && number >= 0 ? Math.round(number) : null;
  };
  const formatCount = (value) => numberFormatter.format(safeInteger(value));
  const isValidEntity = (raw) => Boolean(raw && typeof raw === 'object' && safeMetric(raw.views) !== null);
  const postHeaders = () => ({
    'Content-Type': 'application/json',
    Accept: 'application/json',
    'X-OTT-Requested-With': 'Shopify-Theme-V1',
    ...(csrfToken ? { 'X-CSRF-Token': csrfToken } : {})
  });

  const readJSON = (storage, key, fallback) => {
    try { return JSON.parse(storage.getItem(key) || '') || fallback; } catch (_) { return fallback; }
  };
  const writeJSON = (storage, key, value) => {
    try { storage.setItem(key, JSON.stringify(value)); } catch (_) {}
  };

  const sessionId = (() => {
    try {
      let value = sessionStore?.getItem(sessionKey);
      if (!value) {
        value = crypto.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`;
        sessionStore?.setItem(sessionKey, value);
      }
      return value;
    } catch (_) { return ''; }
  })();

  const createBar = (root) => {
    let bar = root.querySelector(':scope [data-ott-popularity]');
    if (bar) return bar;
    bar = document.createElement('div');
    bar.className = 'ott-v1-popularity';
    bar.hidden = false;
    bar.dataset.ottPopularity = entityKey(root);
    bar.dataset.ottPopularityState = 'pending';
    bar.setAttribute('aria-label', copy.popularity);
    bar.innerHTML = `
      <span class="ott-v1-popularity__metric" title="${copy.popularity}"><span class="ott-v1-popularity__icon" aria-hidden="true">◉</span><b class="ott-v1-popularity__value" data-ott-popularity-count="views">\u2014</b><span class="ott-v1-popularity__label">${copy.views}</span></span>
      <button class="ott-v1-popularity__action" type="button" data-ott-popularity-action="like" aria-label="${copy.like}" aria-pressed="false" disabled hidden><span class="ott-v1-popularity__icon" aria-hidden="true">♡</span><b class="ott-v1-popularity__value" data-ott-popularity-count="likes">\u2014</b><span class="ott-v1-popularity__label">likes</span></button>
      <button class="ott-v1-popularity__action" type="button" data-ott-popularity-action="favorite" aria-label="${copy.favorite}" aria-pressed="false" disabled hidden><span class="ott-v1-popularity__icon" aria-hidden="true">☆</span><b class="ott-v1-popularity__value" data-ott-popularity-count="favorites">\u2014</b><span class="ott-v1-popularity__label">${copy.favorites}</span></button>
      <p class="ott-v1-popularity__status" data-ott-popularity-status aria-live="polite"></p>`;

    const explicitSlot = root.querySelector(':scope [data-ott-popularity-slot]');
    const cardBody = root.querySelector(':scope > .ott372-card__body, :scope .ott320-index__body');
    const productAnchor = root.querySelector('.ottp06__trust');
    const publicActions = root.querySelector('.ott342-actions, .ott416-artist__actions, .ott358-actions');
    if (explicitSlot) explicitSlot.appendChild(bar);
    else if (productAnchor) productAnchor.insertAdjacentElement('afterend', bar);
    else if (publicActions) publicActions.insertAdjacentElement('afterend', bar);
    else if (cardBody) cardBody.appendChild(bar);
    else root.appendChild(bar);
    return bar;
  };

  const normalizeEntity = (raw = {}, previous = {}) => ({
    views: safeMetric(raw.views) ?? safeMetric(previous.views),
    likes: safeMetric(raw.likes) ?? safeMetric(previous.likes),
    favorites: safeMetric(raw.favorites) ?? safeMetric(previous.favorites),
    liked: raw.liked === undefined ? Boolean(previous.liked) : Boolean(raw.liked),
    favorited: raw.favorited === undefined ? Boolean(previous.favorited) : Boolean(raw.favorited)
  });

  const renderKey = (key) => {
    const state = stateByKey.get(key);
    if (!state) return;
    (rootsByKey.get(key) || []).forEach((root) => {
      const bar = createBar(root);
      ['views', 'likes', 'favorites'].forEach((metric) => {
        const node = bar.querySelector(`[data-ott-popularity-count="${metric}"]`);
        if (node) node.textContent = Number.isFinite(state[metric]) ? formatCount(state[metric]) : '\u2014';
      });
      ['like', 'favorite'].forEach((action) => {
        const button = bar.querySelector(`[data-ott-popularity-action="${action}"]`);
        const metric = action === 'like' ? 'likes' : 'favorites';
        const pressed = action === 'like' ? state.liked : state.favorited;
        if (button) {
          button.hidden = !Number.isFinite(state[metric]);
          button.disabled = busyKeys.has(key) || !Number.isFinite(state[metric]);
          button.setAttribute('aria-pressed', pressed ? 'true' : 'false');
        }
      });
      bar.dataset.ottPopularityState = 'ready';
      bar.hidden = false;
      root.classList.add('is-vps-popularity-ready');
    });
  };

  const setStatus = (key, message) => {
    (rootsByKey.get(key) || []).forEach((root) => {
      const node = root.querySelector('[data-ott-popularity-status]');
      if (node) node.textContent = message || '';
    });
  };

  const setBusy = (key, busy) => {
    (rootsByKey.get(key) || []).forEach((root) => {
      const bar = root.querySelector('[data-ott-popularity]');
      if (bar) bar.setAttribute('aria-busy', busy ? 'true' : 'false');
      const state = stateByKey.get(key);
      root.querySelectorAll('[data-ott-popularity-action]').forEach((button) => {
        const metric = button.dataset.ottPopularityAction === 'like' ? 'likes' : 'favorites';
        button.disabled = busy || !Number.isFinite(state?.[metric]);
      });
    });
  };

  const discover = (scope = document) => {
    const found = [];
    if (scope instanceof Element && scope.matches('[data-ott-entity-type][data-ott-entity-id]')) found.push(scope);
    scope.querySelectorAll?.('[data-ott-entity-type][data-ott-entity-id]').forEach((root) => found.push(root));
    found.forEach((root) => {
      if (root.dataset.ottPopularityBound === 'true') return;
      const key = entityKey(root);
      if (!/^(beat|artist):[a-z0-9_-]+$/i.test(key)) return;
      root.dataset.ottPopularityBound = 'true';
      if (!rootsByKey.has(key)) rootsByKey.set(key, []);
      rootsByKey.get(key).push(root);
      createBar(root);
    });
  };

  const pruneRoots = () => {
    rootsByKey.forEach((roots, key) => {
      const connected = roots.filter((root) => root.isConnected);
      if (connected.length) rootsByKey.set(key, connected);
      else {
        rootsByKey.delete(key);
        stateByKey.delete(key);
      }
    });
  };

  const loadCachedCounts = () => {
    const cached = readJSON(sessionStore, cacheKey, {});
    if (!cached.savedAt || Date.now() - cached.savedAt > 60_000 || !cached.entities) return false;
    Object.entries(cached.entities).forEach(([key, entity]) => {
      if (rootsByKey.has(key)) {
        stateByKey.set(key, normalizeEntity(entity));
        renderKey(key);
      }
    });
    return true;
  };

  const scheduleFetchRetry = () => {
    if (statsRetryTimer || statsRetryCount >= 2) return;
    const delay = statsRetryCount === 0 ? 1500 : 4000;
    statsRetryCount += 1;
    statsRetryTimer = window.setTimeout(() => {
      statsRetryTimer = null;
      lastFetchSignature = '';
      boot();
    }, delay);
  };

  const resetFetchRetry = () => {
    statsRetryCount = 0;
    if (statsRetryTimer) window.clearTimeout(statsRetryTimer);
    statsRetryTimer = null;
  };

  const fetchCounts = async () => {
    const keys = Array.from(rootsByKey.keys());
    if (!keys.length) return;
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 5000);
    const url = new URL(`${popularity.baseUrl.replace(/\/$/, '')}/counts`);
    url.searchParams.set('entities', keys.join(','));
    url.searchParams.set('source', 'shopify-v1');
    try {
      const response = await fetch(url, {
        credentials: 'include',
        headers: { Accept: 'application/json' },
        signal: controller.signal
      });
      if (!response.ok) throw new Error(`counts-${response.status}`);
      const payload = await response.json();
      csrfToken = response.headers.get('x-csrf-token') || (typeof payload.csrf_token === 'string' ? payload.csrf_token : '') || csrfToken;
      const entities = payload.entities || payload.data || {};
      const saved = {};
      Object.entries(entities).forEach(([key, entity]) => {
        if (!rootsByKey.has(key) || !isValidEntity(entity)) return;
        const normalized = normalizeEntity(entity, stateByKey.get(key));
        stateByKey.set(key, normalized);
        saved[key] = normalized;
        renderKey(key);
      });
      if (Object.keys(saved).length) {
        const cached = readJSON(sessionStore, cacheKey, {});
        writeJSON(sessionStore, cacheKey, { savedAt: Date.now(), entities: { ...(cached.entities || {}), ...saved } });
      }
      if (keys.some((key) => !Object.prototype.hasOwnProperty.call(saved, key))) scheduleFetchRetry();
      else resetFetchRetry();
      observeViews();
    } catch (_) {
      lastFetchSignature = '';
      scheduleFetchRetry();
    } finally {
      window.clearTimeout(timeout);
    }
  };

  const loginMessage = (key, serverLoginUrl = '') => {
    setStatus(key, 'Connecte-toi au réseau OTTRecord pour synchroniser cette action.');
    const loginUrl = new URL(serverLoginUrl || config.loginUrl || `${config.origin}/connexion`, window.location.origin);
    loginUrl.searchParams.set('return_to', window.location.href);
    (rootsByKey.get(key) || []).forEach((root) => {
      const status = root.querySelector('[data-ott-popularity-status]');
      if (!status || status.querySelector('a')) return;
      status.append(' ');
      const link = document.createElement('a');
      link.href = loginUrl.toString();
      link.rel = 'noopener';
      link.textContent = 'Se connecter ↗';
      status.appendChild(link);
    });
  };

  const act = async (key, action, desired) => {
    const current = stateByKey.get(key);
    if (!current || busyKeys.has(key) || !Number.isFinite(current[action === 'like' ? 'likes' : 'favorites'])) return;
    busyKeys.add(key);
    setBusy(key, true);
    setStatus(key, 'Synchronisation…');
    const entity = splitKey(key);
    try {
      const response = await fetch(`${popularity.baseUrl.replace(/\/$/, '')}/action`, {
        method: 'POST',
        credentials: 'include',
        headers: postHeaders(),
        body: JSON.stringify({ entity_type: entity.type, entity_id: entity.id, action, active: desired, source: 'shopify-theme-v1', return_to: window.location.href })
      });
      if (response.status === 401) {
        const denied = await response.json().catch(() => ({}));
        loginMessage(key, typeof denied.loginUrl === 'string' ? denied.loginUrl : '');
        return;
      }
      if (response.status === 403) {
        setStatus(key, 'Session expirée. Recharge la page pour resynchroniser ton compte.');
        return;
      }
      if (!response.ok) throw new Error(`action-${response.status}`);
      const payload = await response.json().catch(() => ({}));
      if (!isValidEntity(payload.entity)) throw new Error('action-invalid-entity');
      csrfToken = response.headers.get('x-csrf-token') || (typeof payload.csrf_token === 'string' ? payload.csrf_token : '') || csrfToken;
      const next = normalizeEntity(payload.entity);
      stateByKey.set(key, next);
      renderKey(key);
      setStatus(key, desired ? 'Ajouté au réseau OTTRecord.' : 'Retiré de ta sélection.');
    } catch (_) {
      setStatus(key, 'Le réseau est momentanément indisponible. Ta navigation reste intacte.');
    } finally {
      busyKeys.delete(key);
      setBusy(key, false);
    }
  };

  const flushViews = async () => {
    flushTimer = undefined;
    if (viewFlushRunning || !viewQueue.size) return;
    viewFlushRunning = true;
    const keys = Array.from(viewQueue);
    try {
      const response = await fetch(`${popularity.baseUrl.replace(/\/$/, '')}/views`, {
        method: 'POST',
        credentials: 'include',
        keepalive: true,
        headers: postHeaders(),
        body: JSON.stringify({ entities: keys.map(splitKey), session_id: sessionId, source: 'shopify-theme-v1', path: window.location.pathname })
      });
      if (!response.ok) throw new Error(`views-${response.status}`);
      const viewed = new Set(readJSON(sessionStore, viewedKey, []));
      keys.forEach((key) => {
        viewQueue.delete(key);
        viewed.add(key);
        viewedMemory.add(key);
      });
      writeJSON(sessionStore, viewedKey, Array.from(viewed).slice(-300));
      viewFailures = 0;
      const payload = await response.json().catch(() => ({}));
      csrfToken = response.headers.get('x-csrf-token') || (typeof payload.csrf_token === 'string' ? payload.csrf_token : '') || csrfToken;
      const updates = payload.entities || payload.data || {};
      Object.entries(updates).forEach(([key, entity]) => {
        if (!rootsByKey.has(key) || !isValidEntity(entity)) return;
        stateByKey.set(key, normalizeEntity(entity, stateByKey.get(key)));
        renderKey(key);
      });
    } catch (_) {
      viewFailures += 1;
      if (viewFailures <= 2 && !flushTimer) {
        flushTimer = window.setTimeout(flushViews, viewFailures * 1500);
      }
    } finally {
      viewFlushRunning = false;
      if (viewQueue.size && viewFailures === 0 && !flushTimer) {
        flushTimer = window.setTimeout(flushViews, 900);
      }
    }
  };

  const queueView = (key) => {
    const viewed = new Set(readJSON(sessionStore, viewedKey, []));
    if (viewed.has(key) || viewedMemory.has(key) || viewQueue.has(key)) return;
    viewQueue.add(key);
    if (!viewFlushRunning && !flushTimer) flushTimer = window.setTimeout(flushViews, 900);
  };

  const observeViews = () => {
    if (!('IntersectionObserver' in window)) return;
    if (!viewObserver) {
      viewObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          const root = entry.target;
          const existing = viewTimers.get(root);
          if (!entry.isIntersecting) {
            if (existing) window.clearTimeout(existing);
            viewTimers.delete(root);
            return;
          }
          if (existing) return;
          viewTimers.set(root, window.setTimeout(() => {
            queueView(entityKey(root));
            viewObserver.unobserve(root);
            viewTimers.delete(root);
          }, 700));
        });
      }, { threshold: .6 });
    }
    rootsByKey.forEach((roots) => roots.forEach((root) => {
      if (observedRoots.has(root)) return;
      observedRoots.add(root);
      viewObserver.observe(root);
    }));
  };

  document.addEventListener('click', (event) => {
    const actionButton = event.target.closest('[data-ott-popularity-action]');
    if (actionButton) {
      const root = actionButton.closest('[data-ott-entity-type][data-ott-entity-id]');
      if (!root) return;
      event.preventDefault();
      event.stopPropagation();
      const action = actionButton.dataset.ottPopularityAction;
      const key = entityKey(root);
      const state = stateByKey.get(key);
      if (!state) return;
      act(key, action, action === 'like' ? !state.liked : !state.favorited);
      return;
    }
  });

  const boot = (scope = document) => {
    pruneRoots();
    discover(scope);
    loadCachedCounts();
    observeViews();
    const signature = Array.from(rootsByKey.keys()).sort().join(',');
    if (!signature || signature === lastFetchSignature) return;
    if (fetchTask !== undefined) {
      if (fetchTaskIsIdle) window.cancelIdleCallback(fetchTask);
      else window.clearTimeout(fetchTask);
    }
    const runFetch = () => {
      fetchTask = undefined;
      lastFetchSignature = Array.from(rootsByKey.keys()).sort().join(',');
      fetchCounts();
    };
    if ('requestIdleCallback' in window) {
      fetchTaskIsIdle = true;
      fetchTask = window.requestIdleCallback(runFetch, { timeout: 1100 });
    } else {
      fetchTaskIsIdle = false;
      fetchTask = window.setTimeout(runFetch, 180);
    }
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => boot(), { once: true });
  else boot();
  document.addEventListener('shopify:section:load', (event) => boot(event.target));
  document.addEventListener('ott:navigation:complete', () => boot());
  document.addEventListener('ott:catalogue:updated', (event) => boot(event.detail?.root || document));
  window.addEventListener('online', () => {
    boot();
    if (viewQueue.size && !viewFlushRunning && !flushTimer) {
      viewFailures = 0;
      flushTimer = window.setTimeout(flushViews, 900);
    }
  }, { passive: true });
})();
